import platform from 'platform'

import CoreResize from '../../mixins/core-resize'
import AppCarousel3dSlide from '../app-carousel-3d-slide'
import CoreRAF from '../../mixins/core-raf'

export default
{
    name: 'app-carousel-3d',

    components:
    {
        AppCarousel3dSlide
    },

    mixins:
    [
        CoreResize,
        CoreRAF
    ],

    data()
    {
        return {
            index: 0,
            radius: 0,
            fragmentAngle: 0,
            fragmentDistance: 0,
            fullLoop: false,
            maxHeight: 60
        }
    },

    watch:
    {
        index: '_onIndexChange'
    },

    computed:
    {
        slideIndex()
        {
            // Calculate the true slide index even when the index is above or under the limit
            const count = Math.max(8, this.content.length)

            let index = this.index % count

            if (index < 0)
            {
                index = count - (-index % count)
            }

            return index
        },

        hasVisual()
        {
            const currentSlide = this.content[this.slideIndex]

            return currentSlide.video || currentSlide.image
        },

        fallback()
        {
            return platform.name === 'IE' && parseInt(platform.version, 10) <= 11
            // return true
        }
    },

    props:
    {
        content: {
            type: Array,
            default()
            {
                return []
            }
        },

        mousePosSmooth: {
            type: Array,
            default()
            {
                return [0, 0]
            }
        }
    },

    created()
    {
        this.parallax = {}
        this.parallax.x = 0
        this.parallax.y = 0
    },

    mounted()
    {
        this.setParallax()
        this.setSlides()

        if (['Android', 'iOS', 'Windows Phone'].indexOf(platform.os.family) === -1)
        {
            this.setAutoplay()
        }

        if (this.content.length > 1)
        {
            this.setKeyboard()

            if ('ontouchstart' in window || navigator.maxTouchPoints)
            {
                this.setTouch()
            }
        }

        this.$parts = [...this.$el.querySelectorAll('.c-carousel-3d__texts')]
        this.maxHeight = this.getMaxHeight()

        window.addEventListener('resize', () =>
        {
            this.maxHeight = this.getMaxHeight()
        })
    },

    destroyed()
    {
        document.removeEventListener('keydown', this._onKeyDown)
    },

    methods:
    {
        getMaxHeight()
        {
            return this.$parts.reduce(
                (value, $part) => Math.max(value, $part.offsetHeight),
                60
            )
        },

        _onIndexChange(value)
        {
            this.$emit('index-change', value)
        },

        _onKeyDown(event)
        {
            if (event.keyCode === 39 || event.keyCode === 37)
            {
                const bouding = this.$el.getBoundingClientRect()

                if (bouding.top > 0)
                {
                    if (event.keyCode === 39)
                    {
                        this._onNext()
                    }
                    if (event.keyCode === 37)
                    {
                        this._onPrevious()
                    }
                }
            }
        },

        _onPrevious()
        {
            let index = this.index - 1

            // Loop
            if (index < 0 && !this.fullLoop)
            {
                index = this.content.length - 1
            }

            this.index = index

            this.autoplay.reset()
        },

        _onNext()
        {
            let index = this.index + 1

            // Loop
            if (index > this.content.length - 1 && !this.fullLoop)
            {
                index = 0
            }

            this.index = index

            this.autoplay.reset()
        },

        _onSlideClick(index)
        {
            if (index > this.index)
            {
                this.index++
            }
            if (index < this.index)
            {
                this.index--
            }
        },

        onFrame()
        {
            // Parallax
            const x = Math.round(-this.mousePosSmooth[0] * 1000 * 10) / 10
            const y = Math.round(-this.mousePosSmooth[1] * 1000 * 10) / 10

            if (this.parallax.x !== x || this.parallax.y !== y)
            {
                this.parallax.x = x
                this.parallax.y = y

                this.$parallax.style.transform = `translateX(${x}px) translateY(${y}px)`
            }
        },

        setParallax()
        {
            this.$parallax = this.$el.querySelector('.js-parallax')
        },

        setSlides()
        {
            if (this.fallback)
            {
                this.fragmentDistance = window.innerWidth * 0.95
            }
            else
            {
                const count = this.content.length
                const fragmentAngle = 360 / Math.max(8, count)
                const perimeter = Math.max(8, count) * 1400 // Distance between slides

                this.fullLoop = count >= 8
                this.radius = Math.round(perimeter / (Math.PI * 2))
                this.fragmentAngle = fragmentAngle
            }
        },

        setAutoplay()
        {
            // Set up
            this.autoplay = {}
            this.autoplay.startTimeout = null
            this.autoplay.progressTimeout = null
            this.autoplay.$hovers = this.$el.querySelectorAll('.js-autoplay-hover')
            this.autoplay.$progress = this.$el.querySelector('.js-autoplay-progress')

            this.autoplay.$progress.addEventListener('animationend', () =>
            {
                this._onNext()
            })

            // Mouse enter and leave on carousel elements
            for (const $hover of this.autoplay.$hovers)
            {
                $hover.addEventListener('mouseenter', () =>
                {
                    this.autoplay.stop()
                })

                $hover.addEventListener('mouseleave', () =>
                {
                    this.autoplay.start()
                })
            }

            // Stop
            this.autoplay.stop = () =>
            {
                this.autoplay.$progress.classList.add('is-paused')
            }

            // Start
            this.autoplay.start = () =>
            {
                this.autoplay.$progress.classList.remove('is-paused')
            }

            // Reset
            this.autoplay.reset = () =>
            {
                this.autoplay.$progress.classList.remove('is-animated')

                this.autoplay.startTimeout = window.setTimeout(() =>
                {
                    this.autoplay.$progress.classList.add('is-animated')
                }, 60)
            }

            // Modal events
            this.eventHub.$on('application:on-open-modal-video', () =>
            {
                window.setTimeout(this.autoplay.stop, 60)
            })

            this.eventHub.$on('application:on-close-modal-video', () =>
            {
                this.autoplay.start()
            })

            // Start after few seconds
            this.autoplay.startTimeout = window.setTimeout(this.autoplay.reset, 2000)
        },

        setKeyboard()
        {
            document.addEventListener('keydown', this._onKeyDown)
        },

        setTouch()
        {
            this.touch = { $: {} }
            this.touch.down = false
            this.touch.start = { x: null, y: null }
            this.touch.current = { x: null, y: null }
            this.touch.difference = { x: null, y: null }
            this.swipe = { minimum: 20 }

            document.addEventListener('touchstart', (event) =>
            {
                const touch = event.touches[0] || event.changedTouches[0]

                this.touch.start.x = touch.screenX
                this.touch.start.y = touch.screenY

                this.touch.down = true
            })

            document.addEventListener('touchmove', (event) =>
            {
                if (!this.touch.down)
                {
                    return
                }

                const touch = event.touches[0] || event.changedTouches[0]

                this.touch.current.x = touch.screenX
                this.touch.current.y = touch.screenY

                this.touch.difference.x = this.touch.current.x - this.touch.start.x
                this.touch.difference.y = this.touch.current.y - this.touch.start.y

                // Prevent default if horizontal move
                if (Math.abs(this.touch.difference.x) > Math.abs(this.touch.difference.y))
                {
                    event.preventDefault()
                }
            })

            document.addEventListener('touchend', () =>
            {
                if (!this.touch.down)
                {
                    return
                }

                if (Math.abs(this.touch.difference.x) > this.swipe.minimum)
                {
                    if (this.touch.difference.x < 0)
                    {
                        this._onNext()
                    }
                    else
                    {
                        this._onPrevious()
                    }
                }

                this.touch.start.x = null
                this.touch.start.y = null
                this.touch.current.x = null
                this.touch.current.y = null
                this.touch.difference.x = null
                this.touch.difference.y = null
                this.touch.down = false
            })
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/components/app-carousel-3d/script.js